import { useState, useEffect } from 'react';
import { groundsAPI, seriesAPI, divisionsAPI } from '../../services/api';
import { PageHeader, SectionTitle, Field, Spinner, Modal, ConfirmDialog } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  available:   { bg: 'rgba(0,200,150,.18)', border: 'rgba(0,200,150,.4)', text: 'var(--accent)' },
  blocked:     { bg: 'rgba(239,68,68,.18)',  border: 'rgba(239,68,68,.4)',  text: 'var(--red)' },
  restricted:  { bg: 'rgba(245,166,35,.18)', border: 'rgba(245,166,35,.4)', text: 'var(--gold)' },
  none:        { bg: 'var(--card)',           border: 'var(--border)',        text: 'var(--dim)' },
};

const STATUS_CYCLE = ['none', 'available', 'blocked', 'restricted'];

function getWeekendsBetween(start, end) {
  const dates = [];
  const cur = new Date(start);
  cur.setHours(0, 0, 0, 0);
  const endD = new Date(end);
  endD.setHours(23, 59, 59, 999);
  while (cur <= endD) {
    const dow = cur.getDay();
    if (dow === 0 || dow === 6) dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

function fmt(date) {
  return date.toISOString().split('T')[0];
}

function fmtLabel(date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function AvailabilityPage() {
  const { canEdit } = useAuthStore();
  const [seriesList, setSeriesList] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [grounds, setGrounds] = useState([]);
  const [blackouts, setBlackouts] = useState([]);

  const [selSeries, setSelSeries] = useState('');
  const [selDivision, setSelDivision] = useState('');
  const [selGround, setSelGround] = useState('');

  const [dateMap, setDateMap] = useState({}); // { 'YYYY-MM-DD': 'available'|'blocked'|'restricted'|'none' }
  const [playDates, setPlayDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Blackout modal
  const [showBlackoutModal, setShowBlackoutModal] = useState(false);
  const [blackoutForm, setBlackoutForm] = useState({ start_date: '', end_date: '', reason: '' });
  const [deletingBlackout, setDeletingBlackout] = useState(null);

  useEffect(() => { loadSeries(); loadGrounds(); }, []);
  useEffect(() => { if (selSeries) { loadDivisions(selSeries); loadBlackouts(selSeries); buildDates(); } }, [selSeries]);
  useEffect(() => { buildDates(); }, [selSeries, seriesList]);
  useEffect(() => { if (selSeries && selGround) loadAvailability(); }, [selSeries, selGround, selDivision]);

  const loadSeries = async () => {
    try {
      const { data } = await seriesAPI.list();
      setSeriesList(data);
      if (data.length > 0) setSelSeries(data[0].id);
    } catch { toast.error('Failed to load series'); }
  };

  const loadDivisions = async (sid) => {
    try { const { data } = await divisionsAPI.list(sid); setDivisions(data); } catch {}
  };

  const loadGrounds = async () => {
    try {
      const { data } = await groundsAPI.list();
      setGrounds(data);
      if (data.length > 0) setSelGround(data[0].id);
    } catch { toast.error('Failed to load grounds'); }
  };

  const loadBlackouts = async (sid) => {
    try { const { data } = await groundsAPI.getBlackouts(sid); setBlackouts(data); } catch {}
  };

  const buildDates = () => {
    const s = seriesList.find(x => x.id === selSeries);
    if (!s?.start_date || !s?.end_date) return;
    setPlayDates(getWeekendsBetween(s.start_date, s.end_date));
  };

  const loadAvailability = async () => {
    if (!selSeries || !selGround) return;
    setLoading(true);
    try {
      const params = { series_id: selSeries };
      if (selDivision) params.division_id = selDivision;
      const { data } = await groundsAPI.getAvailability(selGround, params);
      const map = {};
      data.forEach(r => { map[r.available_date?.split('T')[0]] = r.status; });
      setDateMap(map);
      setDirty(false);
    } catch { toast.error('Failed to load availability'); }
    finally { setLoading(false); }
  };

  const toggleDate = (dateStr) => {
    if (!canEdit()) return;
    setDateMap(p => {
      const cur = p[dateStr] || 'none';
      const next = STATUS_CYCLE[(STATUS_CYCLE.indexOf(cur) + 1) % STATUS_CYCLE.length];
      return { ...p, [dateStr]: next };
    });
    setDirty(true);
  };

  const bulkMark = (dayOfWeek, status) => {
    if (!canEdit()) return;
    setDateMap(p => {
      const updated = { ...p };
      playDates.forEach(d => {
        if (d.getDay() === dayOfWeek) updated[fmt(d)] = status;
      });
      return updated;
    });
    setDirty(true);
  };

  const clearAll = () => {
    if (!canEdit()) return;
    setDateMap({});
    setDirty(true);
  };

  const saveAll = async () => {
    if (!selSeries || !selGround) return toast.error('Select a series and ground');
    setSaving(true);
    try {
      const records = Object.entries(dateMap)
        .filter(([, status]) => status && status !== 'none')
        .map(([date, status]) => ({
          available_date: date,
          status,
          series_id: selSeries,
          division_id: selDivision || null
        }));
      await groundsAPI.saveAvailability(selGround, records.length > 0 ? records : [{ available_date: null, status: null, series_id: selSeries, division_id: selDivision || null }]);
      toast.success('Availability saved');
      setDirty(false);
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const addBlackout = async () => {
    if (!blackoutForm.start_date || !blackoutForm.end_date || !blackoutForm.reason)
      return toast.error('All fields required');
    try {
      await groundsAPI.addBlackout(selSeries, blackoutForm);
      toast.success('Blackout added');
      setShowBlackoutModal(false);
      setBlackoutForm({ start_date: '', end_date: '', reason: '' });
      loadBlackouts(selSeries);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const deleteBlackout = async () => {
    try {
      await groundsAPI.deleteBlackout(deletingBlackout);
      toast.success('Blackout removed');
      setDeletingBlackout(null);
      loadBlackouts(selSeries);
    } catch { toast.error('Failed to delete blackout'); }
  };

  const selGroundName = grounds.find(g => g.id === selGround)?.name || '';
  const selSeriesObj = seriesList.find(s => s.id === selSeries);
  const availCount = Object.values(dateMap).filter(v => v === 'available').length;
  const blockedCount = Object.values(dateMap).filter(v => v === 'blocked').length;

  return (
    <div>
      <PageHeader icon="◷" title="Ground Availability"
        subtitle="Set available and blocked dates per series per ground."
        actions={canEdit() && (
          <button className="btn btn-primary" onClick={saveAll} disabled={saving || !dirty}>
            {saving ? <><Spinner size={14} /> Saving...</> : dirty ? '● Save Changes' : '✓ Saved'}
          </button>
        )}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20 }}>
        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="card" style={{ padding: 16 }}>
            <Field label="Series">
              <select className="input select" value={selSeries} onChange={e => { setSelSeries(e.target.value); setSelDivision(''); }}>
                {seriesList.map(s => <option key={s.id} value={s.id}>{s.short_name} {s.season_year}</option>)}
              </select>
            </Field>
            <Field label="Division" note="Optional filter">
              <select className="input select" value={selDivision} onChange={e => setSelDivision(e.target.value)}>
                <option value="">All Divisions</option>
                {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Ground">
              <select className="input select" value={selGround} onChange={e => setSelGround(e.target.value)}>
                <option value="">Select ground...</option>
                {grounds.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </Field>

            <hr className="divider" />
            <SectionTitle color="var(--gold)">Bulk Actions</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button className="btn btn-ghost" style={{ fontSize: 12, justifyContent: 'flex-start' }} onClick={() => bulkMark(6, 'available')}>✓ Mark All Saturdays Available</button>
              <button className="btn btn-ghost" style={{ fontSize: 12, justifyContent: 'flex-start' }} onClick={() => bulkMark(0, 'available')}>✓ Mark All Sundays Available</button>
              <button className="btn btn-ghost" style={{ fontSize: 12, justifyContent: 'flex-start' }} onClick={() => bulkMark(6, 'blocked')}>✗ Block All Saturdays</button>
              <button className="btn btn-ghost" style={{ fontSize: 12, justifyContent: 'flex-start' }} onClick={() => bulkMark(0, 'blocked')}>✗ Block All Sundays</button>
              <button className="btn btn-ghost" style={{ fontSize: 12, color: 'var(--red)', justifyContent: 'flex-start' }} onClick={clearAll}>✗ Clear All</button>
            </div>

            {/* Summary */}
            {playDates.length > 0 && (
              <>
                <hr className="divider" />
                <div style={{ fontSize: 11, color: 'var(--mid)', lineHeight: 1.9 }}>
                  <div>Play dates: <span style={{ color: 'var(--text)', fontWeight: 700 }}>{playDates.length}</span></div>
                  <div>Available: <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{availCount}</span></div>
                  <div>Blocked: <span style={{ color: 'var(--red)', fontWeight: 700 }}>{blockedCount}</span></div>
                  <div>Unmarked: <span style={{ color: 'var(--mid)', fontWeight: 700 }}>{playDates.length - availCount - blockedCount}</span></div>
                </div>
              </>
            )}
          </div>

          {/* Blackouts */}
          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <SectionTitle color="var(--red)">Global Blackouts</SectionTitle>
              {canEdit() && <button className="btn btn-ghost btn-sm" onClick={() => setShowBlackoutModal(true)}>+ Add</button>}
            </div>
            {blackouts.length === 0 ? (
              <div style={{ fontSize: 11, color: 'var(--dim)', padding: '8px 0' }}>No blackouts set</div>
            ) : blackouts.map(b => (
              <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
                <div>
                  <span className="tag tag-red" style={{ fontSize: 10 }}>
                    {new Date(b.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {b.start_date !== b.end_date && ` – ${new Date(b.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                  </span>
                  <div style={{ fontSize: 10, color: 'var(--mid)', marginTop: 2 }}>{b.reason}</div>
                </div>
                {canEdit() && (
                  <button onClick={() => setDeletingBlackout(b.id)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 12, padding: '2px 6px' }}>✕</button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Calendar */}
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <SectionTitle>
              {selGroundName || 'Select a ground'} — {selSeriesObj ? `${selSeriesObj.name}` : ''}
            </SectionTitle>
            <div style={{ display: 'flex', gap: 12, fontSize: 11, alignItems: 'center' }}>
              {Object.entries({ available: 'var(--accent)', blocked: 'var(--red)', restricted: 'var(--gold)', none: 'var(--dim)' }).map(([label, color]) => (
                <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: color, opacity: label === 'none' ? 0.4 : 0.7 }} />
                  <span style={{ color: 'var(--mid)', textTransform: 'capitalize' }}>{label}</span>
                </span>
              ))}
              {canEdit() && <span style={{ color: 'var(--dim)', fontSize: 10, marginLeft: 4 }}>Click to cycle status</span>}
            </div>
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner size={28} /></div>
          ) : playDates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--mid)', fontSize: 13 }}>
              Select a series with start/end dates to see the calendar.
            </div>
          ) : (
            <>
              {/* Month groups */}
              {(() => {
                const months = {};
                playDates.forEach(d => {
                  const key = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                  if (!months[key]) months[key] = [];
                  months[key].push(d);
                });
                return Object.entries(months).map(([month, dates]) => (
                  <div key={month} style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mid)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>{month}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {dates.map(d => {
                        const key = fmt(d);
                        const status = dateMap[key] || 'none';
                        const col = STATUS_COLORS[status];
                        const isBlackout = blackouts.some(b => key >= b.start_date?.split('T')[0] && key <= b.end_date?.split('T')[0]);
                        const dow = d.getDay();
                        return (
                          <div key={key} onClick={() => !isBlackout && toggleDate(key)}
                            title={isBlackout ? 'Global blackout' : `Click to change: ${status}`}
                            style={{
                              width: 64, padding: '8px 4px', borderRadius: 7, textAlign: 'center',
                              background: isBlackout ? 'rgba(239,68,68,.08)' : col.bg,
                              border: `1.5px solid ${isBlackout ? 'rgba(239,68,68,.3)' : col.border}`,
                              cursor: isBlackout ? 'not-allowed' : canEdit() ? 'pointer' : 'default',
                              opacity: isBlackout ? 0.6 : 1,
                              transition: 'all .12s'
                            }}>
                            <div style={{ fontSize: 9, color: 'var(--mid)', fontWeight: 600 }}>{dow === 6 ? 'SAT' : 'SUN'}</div>
                            <div style={{ fontSize: 14, fontWeight: 800, color: isBlackout ? 'var(--red)' : col.text, marginTop: 2 }}>{d.getDate()}</div>
                            <div style={{ fontSize: 9, marginTop: 2, color: isBlackout ? 'var(--red)' : col.text, opacity: 0.8, textTransform: 'uppercase' }}>
                              {isBlackout ? 'blackout' : status === 'none' ? '—' : status}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}

              {dirty && canEdit() && (
                <div style={{ marginTop: 16, padding: '10px 14px', background: 'rgba(245,166,35,.1)', border: '1px solid rgba(245,166,35,.3)', borderRadius: 7, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--gold)' }}>● Unsaved changes</span>
                  <button className="btn btn-primary btn-sm" onClick={saveAll} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Now'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add Blackout Modal */}
      {showBlackoutModal && (
        <Modal title="Add Global Blackout" onClose={() => setShowBlackoutModal(false)} width={440}>
          <Field label="Start Date" required>
            <input className="input" type="date" value={blackoutForm.start_date} onChange={e => setBlackoutForm(p => ({ ...p, start_date: e.target.value }))} />
          </Field>
          <Field label="End Date" required>
            <input className="input" type="date" value={blackoutForm.end_date} onChange={e => setBlackoutForm(p => ({ ...p, end_date: e.target.value }))} />
          </Field>
          <Field label="Reason" required>
            <input className="input" placeholder="e.g. Memorial Day Weekend" value={blackoutForm.reason} onChange={e => setBlackoutForm(p => ({ ...p, reason: e.target.value }))} />
          </Field>
          <hr className="divider" />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => setShowBlackoutModal(false)}>Cancel</button>
            <button className="btn btn-danger" onClick={addBlackout}>+ Add Blackout</button>
          </div>
        </Modal>
      )}

      {deletingBlackout && (
        <ConfirmDialog danger
          message="Remove this global blackout? Dates will no longer be blocked across all grounds."
          onConfirm={deleteBlackout}
          onCancel={() => setDeletingBlackout(null)}
        />
      )}
    </div>
  );
}
